import LayerStore from "@giscon/utils.layer-store";
import { CircularProgress } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react";
import APIHelper from "@giscon/utils.api-helper-esri";
import { red } from "@mui/material/colors";
import { useEffect, useRef, useState } from "react";
import useMapServerExportMonitor from "../hooks/useMapServerExportMonitor";
import { getCurrentFilterValues } from "../utils/statistic";
import {
  doesLayerMatchCurrentFilters,
  getBusyLayers,
  getBusyText,
  getErrorText,
  MAP_LAYER_TRANSITION_MESSAGE,
} from "utils/mapBusyIndicatorUtils";

/**
 * Displays loading/error feedback for the map by combining:
 * - LayerStore flags (`loaded`, `updating`, `loadError`)
 * - ArcGIS `view.updating` (drives UI re-render via local React state)
 * - `/MapServer/export` request activity (tracks pending + real failures)
 * - Filter context matching (only shows busy/error for layers matching current selection)
 *
 * UX rules implemented here:
 * - While a busy indicator is shown, previous errors are suppressed (prevents stale errors while a new fetch is in-flight).
 * - Layer errors are only shown for layers that are still not loaded (`loadError && !loaded`), which avoids false errors
 *   caused by canceled requests during rapid layer switching.
 * - Layer errors are scoped to currently visible, on-map, LayerFilter-managed, filter-relevant layers.
 *
 * Layers are tagged with filter context (Kategorie/Parameter/Zeitabschnitt/etc.) when created.
 * This component filters to only show busy indicators for layers matching the current filter
 * selection, preventing irrelevant in-flight requests from showing busy/error UI.
 *
 * @param {Object} props - Component props
 * @param {Object} props.view - ArcGIS MapView instance
 * @param {Object} props.CommunicationStore - MobX store containing filter state
 * @returns {JSX.Element} Loading/error indicator overlay for the map
 */
const MapBusyIndicator = (props) => {
  const { view, CommunicationStore } = props;

  // ArcGIS `view.updating` changes are not MobX-observable, so we mirror them into React state
  // to force a re-render (and show/hide the spinner) during map rendering.
  const [viewUpdating, setViewUpdating] = useState(false);
  const exportPendingRef = useRef(false);

  const {
    exportPendingCount,
    exportError,
    clearExportError,
    formatExportErrorForTooltip,
  } = useMapServerExportMonitor();

  // Get current filter selection for layer context matching
  const currentFilters = getCurrentFilterValues(CommunicationStore) || {};
  const mapLayerTransition =
    CommunicationStore?.getMessagesToID?.({ to: MAP_LAYER_TRANSITION_MESSAGE })
      ?.message || null;

  useEffect(() => {
    const hasPendingExport = exportPendingCount > 0;
    if (hasPendingExport && !exportPendingRef.current) {
      // Clear previous export error as soon as a new export cycle starts.
      clearExportError();
    }
    exportPendingRef.current = hasPendingExport;
  }, [exportPendingCount, clearExportError]);

  // Watch ArcGIS view.updating property and mirror to React state
  useEffect(() => {
    if (!view || typeof view.watch !== "function") {
      setViewUpdating(false);
      return;
    }

    // Initialize once so we don't wait for the first change event.
    setViewUpdating(Boolean(view.updating));

    const handle = view.watch("updating", (isUpdating) => {
      setViewUpdating(Boolean(isUpdating));
    });

    return () => {
      try {
        handle?.remove?.();
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, [view]);

  try {
    // All LayerFilter-managed layers that are on the map.
    // Only include layers managed by LayerFilter to avoid stale layers from other sources.
    // Do not require `visibility` here: LayerFilter adds the next target raster
    // at opacity 0 while it loads, so the not-yet-committed target still needs
    // to contribute its layer name to the busy indicator.
    const allManagedOnMapLayers = LayerStore.layers.filter(
      (l) =>
        APIHelper.isLayerOnMap(LayerStore.map, l) &&
        l.isLayerFilterManaged === true
    );

    const relevantVisibleLayers = allManagedOnMapLayers.filter((layer) =>
      doesLayerMatchCurrentFilters(layer, currentFilters)
    );

    // Rapid switching can cancel in-flight requests; some implementations still flip `loadError`
    // even though a previous (or later) request successfully rendered.
    // We only treat it as an error while it is still unresolved (`loadError && !loaded`).
    const hasUnresolvedLoadError = (layer) =>
      Boolean(layer.loadError && !layer.loaded);

    // Only consider errors for layers that are currently visible + relevant.
    // This avoids showing stale failures for layers the user already switched away from.
    const errorLayers = relevantVisibleLayers.filter(hasUnresolvedLoadError);

    // Layers used for busy tracking: exclude only unresolved load errors
    const relevantLayersForBusy = relevantVisibleLayers.filter(
      (layer) => !hasUnresolvedLoadError(layer)
    );

    const busyBecauseView = Boolean(viewUpdating);

    // Determine which layers are currently busy (updating)
    const busyLayers = getBusyLayers(
      relevantLayersForBusy,
      exportPendingCount,
      busyBecauseView
    );

    // Generate user-facing text for errors and busy states
    const errorText = getErrorText(errorLayers);
    const exportErrorText = formatExportErrorForTooltip(exportError);

    const busyText = getBusyText(busyLayers);

    const transitionBusy = Boolean(mapLayerTransition?.busy);
    const transitionBusyText =
      transitionBusy && mapLayerTransition?.layerName
        ? `${mapLayerTransition.layerName} wird geladen`
        : "";
    const showBusyIndicator =
      transitionBusy ||
      Boolean(busyText) ||
      busyBecauseView ||
      exportPendingCount > 0;
    const busyTitle =
      transitionBusyText ||
      busyText ||
      (showBusyIndicator ? "Karte lädt..." : "");
    // Hide errors while a new busy cycle is visible (prevents stale errors during retry/switch).
    const suppressErrors = showBusyIndicator;
    const showErrorIcon =
      !suppressErrors && (errorLayers.length > 0 || exportError);

    return (
      <div
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          padding: "15px",
        }}
      >
        {showBusyIndicator && (
          <Tooltip title={busyTitle} placement="top">
            <CircularProgress />
          </Tooltip>
        )}

        {showErrorIcon && (
          <Tooltip
            title={exportError ? exportErrorText : errorText}
            placement="top"
          >
            <ErrorIcon fontSize="large" style={{ color: red[500] }} />
          </Tooltip>
        )}

        {LayerStore.basemapUpdating && (
          <Tooltip title={"Hintergrundkarte lädt..."} placement="top">
            <CircularProgress />
          </Tooltip>
        )}
      </div>
    );
  } catch (e) {
    console.error(e);
  }
  return <div></div>;
};
export default observer(MapBusyIndicator);
